// [claude-code 2026-03-11] T5: added /stop command handling
import { useState, useEffect, useCallback, useRef } from "react";
import { Lock } from "lucide-react";
import { filterSkills } from "../../lib/skills";

interface FintheonSlashPickerProps {
  query: string;
  onSelect: (skillId: string) => void;
  onDismiss: () => void;
  onStop?: () => void;
  disabledSkills?: Record<string, { reason: string }>;
}

export function FintheonSlashPicker({
  query,
  onSelect,
  onDismiss,
  onStop,
  disabledSkills,
}: FintheonSlashPickerProps) {
  const [highlightIndex, setHighlightIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const filtered = filterSkills(query);

  // Reset highlight when query changes
  useEffect(() => {
    setHighlightIndex(0);
  }, [query]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const skill = filtered[highlightIndex];
        if (skill && !disabledSkills?.[skill.id]) {
          if (skill.id === "stop" && onStop) {
            onStop();
            onDismiss();
          } else {
            onSelect(skill.id);
          }
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onDismiss();
      }
    },
    [filtered, highlightIndex, onSelect, onDismiss, disabledSkills],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [handleKeyDown]);

  // Scroll highlighted item into view
  useEffect(() => {
    const el = listRef.current?.children[highlightIndex] as
      HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex]);

  if (filtered.length === 0) {
    return (
      <div className="flex max-h-[220px] min-h-0 flex-col overflow-hidden px-3 py-3">
        <div className="px-3 py-4 text-center text-[11px] text-[#f0ead6]/38">
          No matching skills for "/{query}"
        </div>
      </div>
    );
  }

  return (
    <div className="flex max-h-[220px] min-h-0 flex-col overflow-hidden px-3 py-3">
      <div ref={listRef} className="max-h-[184px] overflow-y-auto">
        {filtered.map((skill, idx) => {
          const Icon = skill.icon;
          const highlighted = idx === highlightIndex;
          const disabled = disabledSkills?.[skill.id];
          return (
            <button
              key={skill.id}
              onMouseEnter={() => setHighlightIndex(idx)}
              onClick={() => {
                if (disabled) return;
                if (skill.id === "stop" && onStop) {
                  onStop();
                  onDismiss();
                } else {
                  onSelect(skill.id);
                }
              }}
              className={`flex min-h-[42px] w-full items-start gap-2.5 rounded-md px-2 py-2 transition-colors ${
                disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
              }`}
              style={
                highlighted && !disabled
                  ? {
                      background: `linear-gradient(90deg, ${skill.color}20 0%, ${skill.color}10 62%, transparent 100%)`,
                    }
                  : undefined
              }
            >
              <div className="mt-0.5 flex-shrink-0">
                {disabled ? (
                  <Lock size={13} className="text-[#f0ead6]/32" />
                ) : (
                  <Icon
                    size={13}
                    style={{
                      color: highlighted
                        ? skill.color
                        : "rgba(240,234,214,0.38)",
                    }}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <span
                  className={`block truncate text-[11px] font-medium ${highlighted && !disabled ? "text-[#f0ead6]/86" : "text-[#f0ead6]/66"}`}
                >
                  /{skill.id}
                </span>
                <span className="mt-0.5 block truncate text-[10px] text-[#f0ead6]/36">
                  {disabled ? disabled.reason : skill.description}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
